import { useEffect, useMemo, useRef, useState } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Settings } from "lucide-react";
import { cn } from "src/lib/utils";
import type { ContentDocument } from "src/services/diagnosticService";

interface ContentBlocksProps {
  content: ContentDocument | string | null | undefined;
  showTools?: boolean;
}

function MathText({ latex, display }: { latex: string; display: boolean }) {
  const html = useMemo(
    () => katex.renderToString(latex, { throwOnError: false, displayMode: display, trust: false }),
    [latex, display],
  );
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function Inline({ nodes }: { nodes: Array<Record<string, unknown>> }) {
  return (
    <>
      {nodes.map((node, index) => {
        if (node.type === "math" && typeof node.latex === "string") {
          return <MathText key={index} latex={node.latex} display={false} />;
        }
        return <span key={index}>{typeof node.text === "string" ? node.text : ""}</span>;
      })}
    </>
  );
}

function GeoGebraEmbed({
  materialId,
  altText,
  width: rawWidth,
  height: rawHeight,
  showTools = true,
}: {
  materialId: string;
  altText: string;
  width?: number;
  height?: number;
  showTools?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Authoring canvas dimensions: default to 1000x600 as per Spec §7.2.1
  // When tools & drawer are enabled, add room for the drawer so the canvas isn't squashed
  const baseWidth = typeof rawWidth === "number" && rawWidth >= 200 && rawWidth <= 1200 ? rawWidth : 1000;
  const targetWidth = showTools ? baseWidth + 80 : baseWidth;
  const targetHeight = typeof rawHeight === "number" && rawHeight >= 200 && rawHeight <= 1200 ? rawHeight : 600;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateScale = () => {
      const availableWidth = el.clientWidth;
      if (availableWidth > 0 && availableWidth < targetWidth) {
        setScale(availableWidth / targetWidth);
      } else {
        setScale(1);
      }
    };

    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [targetWidth]);

  // Spec-compliant GeoGebra iframe URL:
  // When showTools is true (default): enables toolbars, algebra inputs, and drawer controls
  // When showTools is false: keeps them hidden so the canvas is clean and not displaced
  const src = `https://www.geogebra.org/material/iframe/id/${encodeURIComponent(
    materialId
  )}/width/${targetWidth}/height/${targetHeight}/border/888888/rc/false/ai/${showTools}/sdz/false/smb/false/stb/${showTools}/stbh/false/ld/false/sri/false/ctl/${showTools}`;

  return (
    <div ref={containerRef} className="w-full relative overflow-hidden bg-white">
      <div
        className="w-full relative overflow-hidden bg-white"
        style={{
          height: `${Math.round(targetHeight * scale)}px`,
        }}
      >
        <div
          style={{
            width: `${targetWidth}px`,
            height: `${targetHeight}px`,
            transform: scale < 1 ? `scale(${scale})` : undefined,
            transformOrigin: "top left",
          }}
        >
          <iframe
            src={src}
            title={altText}
            width={targetWidth}
            height={targetHeight}
            className="border-0 bg-white"
            style={{
              width: `${targetWidth}px`,
              height: `${targetHeight}px`,
            }}
            loading="lazy"
            allowFullScreen
          >
            <p className="p-4 text-xs text-slate-600">{altText}</p>
          </iframe>
        </div>
      </div>
    </div>
  );
}

export function ContentBlocks({ content, showTools = true }: ContentBlocksProps) {
  if (!content) return null;
  if (typeof content === "string") return <p>{content}</p>;
  const blocks = Array.isArray(content.blocks) ? content.blocks : [];

  return (
    <div className="space-y-3 text-base leading-relaxed text-[#241432]">
      {blocks.map((block, index) => {
        if (block.type === "blanks") return null;
        if (block.type === "math" && typeof block.latex === "string") {
          return (
            <p key={index} className="overflow-x-auto">
              <MathText latex={block.latex} display={block.display !== false} />
            </p>
          );
        }
        if (block.type === "heading") {
          return (
            <h3 key={index} className="font-roboto text-lg font-semibold text-[#3e1168]">
              {typeof block.text === "string" ? block.text : <Inline nodes={Array.isArray(block.content) ? block.content as Array<Record<string, unknown>> : []} />}
            </h3>
          );
        }
        if (block.type === "list" && Array.isArray(block.items)) {
          return (
            <ul key={index} className="list-disc space-y-1 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  {typeof item === "string"
                    ? item
                    : <Inline nodes={Array.isArray(item) ? item as Array<Record<string, unknown>> : []} />}
                </li>
              ))}
            </ul>
          );
        }
        if (block.type === "image" && typeof block.url === "string") {
          return (
            <img
              key={index}
              src={block.url}
              alt={typeof block.alt === "string" ? block.alt : ""}
              className="max-w-full rounded-md"
            />
          );
        }
        if (
          block.type === "embed" &&
          block.provider === "geogebra" &&
          typeof block.material_id === "string" &&
          /^[A-Za-z0-9]+$/.test(block.material_id)
        ) {
          const altText = typeof block.alt === "string" && block.alt.trim() ? block.alt : "Interactive GeoGebra figure";
          const width = typeof block.width === "number" ? block.width : undefined;
          const height = typeof block.height === "number" ? block.height : undefined;

          return (
            <GeoGebraEmbed
              key={index}
              materialId={block.material_id}
              altText={altText}
              width={width}
              height={height}
              showTools={showTools}
            />
          );
        }
        const nodes = Array.isArray(block.content) ? block.content as Array<Record<string, unknown>> : [];
        return <p key={index}><Inline nodes={nodes} /></p>;
      })}
    </div>
  );
}

export function fibBlanks(content: ContentDocument | null | undefined): { id: string; label: string }[] {
  const blocks = Array.isArray(content?.blocks) ? content.blocks : [];
  const blanks = blocks.find((block) => block.type === "blanks");
  const items = Array.isArray(blanks?.items) ? blanks.items : [];
  const parsed = items.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const record = item as Record<string, unknown>;
    if (typeof record.id !== "string" || !record.id.trim()) return [];
    return [{ id: record.id, label: typeof record.label === "string" && record.label.trim() ? record.label : "Your answer" }];
  });
  return parsed.length > 0 ? parsed : [{ id: "answer", label: "Your answer" }];
}

export function hasFigureBlock(content: ContentDocument | string | null | undefined): boolean {
  if (!content || typeof content === "string") return false;
  const blocks = Array.isArray(content.blocks) ? content.blocks : [];
  return blocks.some(
    (b) =>
      b.type === "image" ||
      (b.type === "embed" && b.provider === "geogebra") ||
      (b.type === "figure")
  );
}

export function hasGeoGebraBlock(content: ContentDocument | string | null | undefined): boolean {
  if (!content || typeof content === "string") return false;
  const blocks = Array.isArray(content.blocks) ? content.blocks : [];
  return blocks.some((b) => b.type === "embed" && b.provider === "geogebra");
}

export function splitFigureAndTextBlocks(content: ContentDocument | string | null | undefined): {
  figureBlocks: ContentDocument | null;
  textBlocks: ContentDocument;
} {
  if (!content || typeof content === "string") {
    return {
      figureBlocks: null,
      textBlocks: { blocks: typeof content === "string" ? [{ type: "paragraph", content: [{ text: content }] }] : [] },
    };
  }
  const allBlocks = Array.isArray(content.blocks) ? content.blocks : [];
  const figures = allBlocks.filter(
    (b) =>
      b.type === "image" ||
      (b.type === "embed" && b.provider === "geogebra") ||
      (b.type === "figure")
  );
  const text = allBlocks.filter(
    (b) =>
      b.type !== "image" &&
      !(b.type === "embed" && b.provider === "geogebra") &&
      b.type !== "figure"
  );

  return {
    figureBlocks: figures.length > 0 ? { blocks: figures } : null,
    textBlocks: { blocks: text },
  };
}

export default ContentBlocks;
